import { useState, useEffect } from 'react';
import api, { getJahresabrechnung, getProperties, getJahresabrechnungExcelUrl, getJahresabrechnungPdfUrl } from '../services/api';
import { FileText, Download, Check, Warning, CaretDown, CaretUp, CalendarBlank, FileXls, Users, House, EnvelopeSimple, PaperPlaneTilt, X as XIcon } from '@phosphor-icons/react';

export default function Jahresabrechnung() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [propertyId, setPropertyId] = useState('');
  const [properties, setProperties] = useState([]);
  const [report, setReport] = useState(null);      // old endpoint (multi-property overview)
  const [reportV2, setReportV2] = useState(null);  // new endpoint (single property + tenants)
  const [loading, setLoading] = useState(false);
  const [expandedKs, setExpandedKs] = useState({});
  const [expandedTenant, setExpandedTenant] = useState({});
  const [showSendDialog, setShowSendDialog] = useState(false);

  useEffect(() => {
    getProperties().then(res => setProperties(res.data)).catch(console.error);
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      if (propertyId) {
        const res = await api.get(`/reports/jahresabrechnung/v2?year=${year}&property_id=${propertyId}`);
        setReportV2(res.data);
        setReport(null);
      } else {
        const res = await getJahresabrechnung(year, undefined);
        setReport(res.data);
        setReportV2(null);
      }
    } catch (err) {
      console.error('Failed to load report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [year, propertyId]);

  const handleDownloadPdf = (tenantId) => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/reports/jahresabrechnung/v2/pdf`);
    url.searchParams.set('year', year);
    if (propertyId) url.searchParams.set('property_id', propertyId);
    if (tenantId) url.searchParams.set('tenant_id', tenantId);
    // Use the cookie-based session (relies on api.js axios for the bearer token if any). For now, navigate.
    fetch(url, { credentials: 'include', headers: api.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Nebenkostenabrechnung_${year}${tenantId ? '_Mieter' : ''}.pdf`;
        link.click();
      });
  };

  const handleDownloadExcel = () => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/reports/jahresabrechnung/v2/excel`);
    url.searchParams.set('year', year);
    if (propertyId) url.searchParams.set('property_id', propertyId);
    fetch(url, { credentials: 'include', headers: api.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Nebenkostenabrechnung_${year}.xlsx`;
        link.click();
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const toggleExpand = (ksName) => {
    setExpandedKs(prev => ({ ...prev, [ksName]: !prev[ksName] }));
  };

  const toggleTenant = (tid) => {
    setExpandedTenant(prev => ({ ...prev, [tid]: !prev[tid] }));
  };

  const verteilLabel = (v) => {
    if (!v) return v;
    if (v === 'verbrauch') return 'nach Verbrauch (Zähler)';
    if (typeof v === 'string' && v.startsWith('flaeche (Verbrauch')) return 'nach Fläche (kein Zähler)';
    return ({
      flaeche: 'nach Fläche',
      personen: 'nach Personen',
      einheiten: 'pro Einheit',
    }[v] || v);
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const umlagefaehigeKs = report?.kostenstellen?.filter(k => k.umlagefaehig) || [];
  const nichtUmlagefaehigeKs = report?.kostenstellen?.filter(k => !k.umlagefaehig) || [];

  // ---- V2 view (when a property is selected) ----
  if (reportV2) {
    const v2 = reportV2;
    const s = v2.summary;
    const umlKs = v2.kostenstellen.filter(k => k.umlagefaehig);
    const nichtUmlKs = v2.kostenstellen.filter(k => !k.umlagefaehig);

    return (
      <div className="space-y-4 sm:space-y-6" data-testid="jahresabrechnung-v2-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
              Jahresabrechnung {v2.year}
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base flex items-center gap-2">
              <House weight="bold" className="w-4 h-4" /> {v2.property.name} — {v2.property.address}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button onClick={handleDownloadExcel} className="border border-emerald-700 text-emerald-700 px-4 sm:px-6 py-2.5 font-medium hover:bg-emerald-50 flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="download-excel-button">
              <FileXls weight="bold" className="w-4 h-4" /> Excel
            </button>
            <button onClick={() => handleDownloadPdf()} className="bg-slate-900 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="download-pdf-button">
              <Download weight="bold" className="w-4 h-4" /> PDF (alle Mieter)
            </button>
            <button onClick={() => setShowSendDialog(true)} className="bg-violet-700 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-violet-800 flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="send-abrechnung-button">
              <PaperPlaneTilt weight="bold" className="w-4 h-4" /> Versenden
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-2">
            <CalendarBlank weight="bold" className="w-5 h-5 text-slate-400" />
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none font-mono font-bold text-lg" data-testid="year-select">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-full border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none" data-testid="property-filter-select">
              <option value="">Alle Immobilien (Übersicht)</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <SumCard color="emerald" label="Umlagefähig" value={formatCurrency(s.umlagefaehig_summe)} hint="auf Mieter umlegbar" />
          <SumCard color="amber" label="Nicht umlagefähig" value={formatCurrency(s.nicht_umlagefaehig_summe)} hint="Vermieter trägt" />
          <SumCard color="slate" label="Soll-Vorauszahlungen" value={formatCurrency(s.summe_soll_vorauszahlungen)} hint={`${s.anzahl_mieter} Mieter`} />
          <SumCard color="violet" label="Ist-Vorauszahlungen" value={formatCurrency(s.summe_ist_vorauszahlungen)} hint="tatsächlich erhalten" />
        </div>

        {/* Tenant Abrechnung */}
        <div className="bg-white border border-slate-200" data-testid="tenant-abrechnung-section">
          <div className="px-4 sm:px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
              <Users weight="bold" className="w-5 h-5" /> Abrechnung pro Mieter
            </h2>
            <div className="text-xs text-slate-500">
              {s.summe_nachzahlungen > 0 && <span className="mr-3">Nachzahlung: <strong className="text-red-700">{formatCurrency(s.summe_nachzahlungen)}</strong></span>}
              {s.summe_guthaben > 0 && <span>Guthaben: <strong className="text-emerald-700">{formatCurrency(s.summe_guthaben)}</strong></span>}
            </div>
          </div>
          {v2.mieter.length === 0 ? (
            <div className="p-6 text-center text-slate-500">Keine Mieter für diese Immobilie erfasst.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {v2.mieter.map(m => (
                <div key={m.tenant_id} data-testid={`mieter-${m.tenant_id}`}>
                  <div className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 flex-wrap gap-2" onClick={() => toggleTenant(m.tenant_id)}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-2 h-12 ${m.ergebnis === 'guthaben' ? 'bg-emerald-500' : m.ergebnis === 'nachzahlung' ? 'bg-red-500' : 'bg-slate-300'}`}></div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{m.tenant_name}</p>
                        <p className="text-xs text-slate-500 truncate">{m.unit_name} · {m.flaeche_qm}m² · {m.personen} Pers. · {m.months_in_year}/12 Mon.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 text-sm flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-slate-500">Umlage-Anteil</p>
                        <p className="font-mono font-bold">{formatCurrency(m.anteil_umlagefaehig)}</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-slate-500">Ist-VZ</p>
                        <p className="font-mono">{formatCurrency(m.ist_vorauszahlungen_jahr)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">{m.ergebnis === 'guthaben' ? 'Guthaben' : m.ergebnis === 'nachzahlung' ? 'Nachzahlung' : 'Saldo'}</p>
                        <p className={`font-mono font-bold ${m.saldo > 0.005 ? 'text-emerald-700' : m.saldo < -0.005 ? 'text-red-700' : 'text-slate-500'}`}>
                          {m.saldo >= 0 ? '+' : ''}{formatCurrency(m.saldo)}
                        </p>
                      </div>
                      {expandedTenant[m.tenant_id] ? <CaretUp className="w-4 h-4 text-slate-400" /> : <CaretDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                  {expandedTenant[m.tenant_id] && (
                    <div className="px-4 sm:px-6 pb-6 bg-slate-50/50">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* VZ-Block */}
                        <div className="bg-white border border-slate-200 p-4">
                          <h3 className="font-bold text-sm mb-3 text-slate-800">Vorauszahlungen</h3>
                          <table className="w-full text-sm">
                            <tbody className="divide-y divide-slate-100">
                              <tr><td className="py-2 text-slate-600">Soll NK ({(m.soll_nk_monthly).toFixed(2)} €/Mo × {m.months_in_year})</td><td className="text-right font-mono">{formatCurrency(m.soll_nk_jahr)}</td></tr>
                              <tr><td className="py-2 text-slate-600">Soll Heizung ({(m.soll_heizung_monthly).toFixed(2)} €/Mo × {m.months_in_year})</td><td className="text-right font-mono">{formatCurrency(m.soll_heizung_jahr)}</td></tr>
                              <tr className="font-bold"><td className="py-2 text-slate-800">Soll Gesamt</td><td className="text-right font-mono">{formatCurrency(m.soll_vorauszahlungen_jahr)}</td></tr>
                              <tr><td className="py-2 text-slate-600">Ist NK erhalten</td><td className="text-right font-mono text-emerald-700">{formatCurrency(m.ist_nk_jahr)}</td></tr>
                              <tr><td className="py-2 text-slate-600">Ist Heizung erhalten</td><td className="text-right font-mono text-emerald-700">{formatCurrency(m.ist_heizung_jahr)}</td></tr>
                              <tr className="font-bold border-t-2 border-slate-300"><td className="py-2 text-slate-800">Ist Gesamt erhalten</td><td className="text-right font-mono text-emerald-700">{formatCurrency(m.ist_vorauszahlungen_jahr)}</td></tr>
                              <tr><td className="py-2 text-slate-600">Ist Kaltmiete erhalten</td><td className="text-right font-mono text-slate-500">{formatCurrency(m.ist_kaltmiete_jahr)}</td></tr>
                            </tbody>
                          </table>
                        </div>
                        {/* Kosten-Anteile */}
                        <div className="bg-white border border-slate-200 p-4">
                          <h3 className="font-bold text-sm mb-3 text-slate-800">Anteile umlagefähiger Kosten</h3>
                          <table className="w-full text-xs">
                            <thead className="border-b border-slate-200">
                              <tr className="text-slate-500 uppercase">
                                <th className="text-left py-1 font-medium">Kostenstelle</th>
                                <th className="text-right py-1 font-medium">%</th>
                                <th className="text-right py-1 font-medium">Anteil</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {m.kosten_anteile.map((k, idx) => (
                                <tr key={idx}>
                                  <td className="py-1.5"><div className="text-slate-800 font-medium">{k.name}</div><div className="text-slate-400">{verteilLabel(k.verteilerschluessel)} · von {formatCurrency(k.gesamt_kosten)}</div></td>
                                  <td className="text-right font-mono text-slate-500">{k.anteil_prozent}%</td>
                                  <td className="text-right font-mono text-slate-900">{formatCurrency(k.anteil_betrag)}</td>
                                </tr>
                              ))}
                              <tr className="font-bold border-t-2 border-slate-300">
                                <td className="py-2">Gesamt-Umlage</td>
                                <td></td>
                                <td className="text-right font-mono">{formatCurrency(m.anteil_umlagefaehig)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className={`mt-4 p-3 ${m.ergebnis === 'guthaben' ? 'bg-emerald-50 border-emerald-200' : m.ergebnis === 'nachzahlung' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'} border text-sm`}>
                        {m.ergebnis === 'guthaben' && <span><strong>Guthaben:</strong> Mieter zahlte {formatCurrency(m.ist_vorauszahlungen_jahr)} an Vorauszahlungen, Anteil umlagefähiger Kosten beträgt nur {formatCurrency(m.anteil_umlagefaehig)} — Erstattung an Mieter: <strong className="text-emerald-700">{formatCurrency(m.saldo)}</strong></span>}
                        {m.ergebnis === 'nachzahlung' && <span><strong>Nachzahlung:</strong> Mieter zahlte {formatCurrency(m.ist_vorauszahlungen_jahr)}, Anteil umlagefähiger Kosten ist aber {formatCurrency(m.anteil_umlagefaehig)} — Mieter muss nachzahlen: <strong className="text-red-700">{formatCurrency(-m.saldo)}</strong></span>}
                        {m.ergebnis === 'ausgeglichen' && <span>Ausgeglichen — keine Nachzahlung/Erstattung.</span>}
                      </div>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadPdf(m.tenant_id); }}
                          className="text-xs bg-slate-900 text-white px-3 py-2 hover:bg-slate-800 inline-flex items-center gap-2"
                          data-testid={`tenant-pdf-${m.tenant_id}`}
                        >
                          <Download weight="bold" className="w-3 h-3" /> PDF für diesen Mieter
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Kostenstellen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white border border-emerald-200">
            <div className="px-4 sm:px-6 py-4 bg-emerald-50 border-b border-emerald-200">
              <h2 className="font-heading text-base font-bold text-emerald-800 flex items-center gap-2">
                <Check weight="bold" className="w-5 h-5" /> Umlagefähig
              </h2>
            </div>
            {umlKs.length === 0 ? <div className="p-6 text-center text-slate-500 text-sm">Keine</div> : (
              <div className="divide-y divide-emerald-100">
                {umlKs.map((k) => (
                  <div key={k.name} className="px-4 sm:px-6 py-3 flex items-center justify-between text-sm">
                    <div><span className="font-medium text-slate-900">{k.name}</span><span className="text-xs text-slate-500 ml-2">({verteilLabel(k.verteilerschluessel)})</span></div>
                    <span className="font-mono font-bold text-emerald-700">{formatCurrency(k.gesamt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white border border-amber-200">
            <div className="px-4 sm:px-6 py-4 bg-amber-50 border-b border-amber-200">
              <h2 className="font-heading text-base font-bold text-amber-800 flex items-center gap-2">
                <Warning weight="bold" className="w-5 h-5" /> Nicht umlagefähig
              </h2>
            </div>
            {nichtUmlKs.length === 0 ? <div className="p-6 text-center text-slate-500 text-sm">Keine</div> : (
              <div className="divide-y divide-amber-100">
                {nichtUmlKs.map((k) => (
                  <div key={k.name} className="px-4 sm:px-6 py-3 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-900">{k.name}</span>
                    <span className="font-mono font-bold text-amber-700">{formatCurrency(k.gesamt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Send Dialog */}
        {showSendDialog && (
          <SendDialog
            onClose={() => setShowSendDialog(false)}
            mieter={v2.mieter}
            year={year}
            propertyId={propertyId}
            propertyName={v2.property.name}
            onSent={() => { setShowSendDialog(false); loadReport(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="jahresabrechnung-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Jahresabrechnung
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">
            Nebenkostenübersicht nach BetrKV §2
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={handleDownloadExcel}
            disabled={!report || report.kostenstellen?.length === 0}
            className="border border-emerald-700 text-emerald-700 px-4 sm:px-6 py-2.5 font-medium hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="download-excel-button"
          >
            <FileXls weight="bold" className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={!report || report.kostenstellen?.length === 0}
            className="bg-slate-900 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto"
            data-testid="download-pdf-button"
          >
            <Download weight="bold" className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-2">
          <CalendarBlank weight="bold" className="w-5 h-5 text-slate-400" />
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none font-mono font-bold text-lg"
            data-testid="year-select"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            className="w-full border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none"
            data-testid="property-filter-select"
          >
            <option value="">Alle Immobilien</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Check weight="bold" className="w-5 h-5 text-emerald-600" />
                <span className="text-xs tracking-widest uppercase font-bold text-emerald-700">Umlagefähig</span>
              </div>
              <p className="font-mono text-2xl sm:text-3xl font-black tracking-tighter text-emerald-700">
                {formatCurrency(report.umlagefaehig_summe)}
              </p>
              <p className="text-xs text-emerald-600 mt-1">Auf Mieter umlegbar</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Warning weight="bold" className="w-5 h-5 text-amber-600" />
                <span className="text-xs tracking-widest uppercase font-bold text-amber-700">Nicht umlagefähig</span>
              </div>
              <p className="font-mono text-2xl sm:text-3xl font-black tracking-tighter text-amber-700">
                {formatCurrency(report.nicht_umlagefaehig_summe)}
              </p>
              <p className="text-xs text-amber-600 mt-1">Vermieter trägt</p>
            </div>
            <div className="bg-slate-100 border border-slate-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText weight="bold" className="w-5 h-5 text-slate-600" />
                <span className="text-xs tracking-widest uppercase font-bold text-slate-600">Gesamt</span>
              </div>
              <p className="font-mono text-2xl sm:text-3xl font-black tracking-tighter text-slate-900">
                {formatCurrency(report.gesamt_summe)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{report.kostenstellen?.length || 0} Kostenstellen</p>
            </div>
          </div>

          {/* Umlagefähige Kosten */}
          <div className="bg-white border border-emerald-200">
            <div className="px-4 sm:px-6 py-4 bg-emerald-50 border-b border-emerald-200">
              <h2 className="font-heading text-lg font-bold text-emerald-800 flex items-center gap-2">
                <Check weight="bold" className="w-5 h-5" />
                Umlagefähige Kosten nach BetrKV §2
              </h2>
            </div>
            {umlagefaehigeKs.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                Keine umlagefähigen Kosten für diesen Zeitraum erfasst.
              </div>
            ) : (
              <div className="divide-y divide-emerald-100">
                {umlagefaehigeKs.map((ks) => (
                  <div key={ks.name} data-testid={`ks-row-${ks.name}`}>
                    <div 
                      className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-emerald-50/50"
                      onClick={() => toggleExpand(ks.name)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 flex items-center justify-center text-emerald-600 font-mono text-xs font-bold">
                          {ks.anzahl_belege}
                        </div>
                        <span className="font-medium text-slate-900">{ks.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono font-bold text-emerald-700">{formatCurrency(ks.summe)}</span>
                        {expandedKs[ks.name] ? (
                          <CaretUp weight="bold" className="w-4 h-4 text-slate-400" />
                        ) : (
                          <CaretDown weight="bold" className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </div>
                    {expandedKs[ks.name] && ks.belege && (
                      <div className="px-4 sm:px-6 pb-4 bg-emerald-50/30">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-slate-500 uppercase">
                              <th className="text-left py-2">Beleg</th>
                              <th className="text-left py-2">Datum</th>
                              <th className="text-right py-2">Betrag</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-emerald-100">
                            {ks.belege.map((beleg, idx) => (
                              <tr key={idx}>
                                <td className="py-2 text-slate-700">{beleg.vendor || 'Unbekannt'}</td>
                                <td className="py-2 text-slate-500">{beleg.date || '—'}</td>
                                <td className="py-2 text-right font-mono text-slate-900">{formatCurrency(beleg.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Nicht umlagefähige Kosten */}
          <div className="bg-white border border-amber-200">
            <div className="px-4 sm:px-6 py-4 bg-amber-50 border-b border-amber-200">
              <h2 className="font-heading text-lg font-bold text-amber-800 flex items-center gap-2">
                <Warning weight="bold" className="w-5 h-5" />
                Nicht umlagefähige Kosten
              </h2>
            </div>
            {nichtUmlagefaehigeKs.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                Keine nicht umlagefähigen Kosten für diesen Zeitraum erfasst.
              </div>
            ) : (
              <div className="divide-y divide-amber-100">
                {nichtUmlagefaehigeKs.map((ks) => (
                  <div key={ks.name} data-testid={`ks-row-${ks.name}`}>
                    <div 
                      className="px-4 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-amber-50/50"
                      onClick={() => toggleExpand(ks.name)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-amber-100 flex items-center justify-center text-amber-600 font-mono text-xs font-bold">
                          {ks.anzahl_belege}
                        </div>
                        <span className="font-medium text-slate-900">{ks.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono font-bold text-amber-700">{formatCurrency(ks.summe)}</span>
                        {expandedKs[ks.name] ? (
                          <CaretUp weight="bold" className="w-4 h-4 text-slate-400" />
                        ) : (
                          <CaretDown weight="bold" className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </div>
                    {expandedKs[ks.name] && ks.belege && (
                      <div className="px-4 sm:px-6 pb-4 bg-amber-50/30">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-slate-500 uppercase">
                              <th className="text-left py-2">Beleg</th>
                              <th className="text-left py-2">Datum</th>
                              <th className="text-right py-2">Betrag</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-amber-100">
                            {ks.belege.map((beleg, idx) => (
                              <tr key={idx}>
                                <td className="py-2 text-slate-700">{beleg.vendor || 'Unbekannt'}</td>
                                <td className="py-2 text-slate-500">{beleg.date || '—'}</td>
                                <td className="py-2 text-right font-mono text-slate-900">{formatCurrency(beleg.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">Hinweis zur Nebenkostenabrechnung</p>
            <p>Nur Belege mit Status "Zugeordnet" oder "Gebucht" werden in der Abrechnung berücksichtigt. Stellen Sie sicher, dass alle Belege korrekt kategorisiert und einer Kostenstelle zugewiesen sind.</p>
          </div>
        </>
      ) : (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <FileText weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Keine Daten</h3>
          <p className="text-slate-500">Für den ausgewählten Zeitraum sind keine Belege vorhanden.</p>
        </div>
      )}
    </div>
  );
}

function SumCard({ color, label, value, hint }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-100 border-slate-200 text-slate-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
  }[color] || 'bg-white border-gray-200 text-slate-700';
  return (
    <div className={`${colors} border p-4`}>
      <p className="text-xs tracking-widest uppercase font-bold">{label}</p>
      <p className="font-mono text-xl sm:text-2xl font-black tracking-tighter mt-1">{value}</p>
      {hint && <p className="text-xs opacity-70 mt-1">{hint}</p>}
    </div>
  );
}

function SendDialog({ onClose, mieter, year, propertyId, propertyName, onSent }) {
  const [selected, setSelected] = useState(() => Object.fromEntries(mieter.map(m => [m.tenant_id, true])));
  const [sendEmail, setSendEmail] = useState(true);
  const [sendPortal, setSendPortal] = useState(true);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('');

  const filtered = mieter.filter(m => !filter || m.tenant_name.toLowerCase().includes(filter.toLowerCase()) || m.unit_name?.toLowerCase().includes(filter.toLowerCase()));
  const selectedIds = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
  const allChecked = filtered.every(m => selected[m.tenant_id]);

  const toggleAll = () => {
    const next = { ...selected };
    filtered.forEach(m => { next[m.tenant_id] = !allChecked; });
    setSelected(next);
  };

  const send = async () => {
    if (selectedIds.length === 0) return;
    if (!sendEmail && !sendPortal) {
      alert('Bitte mindestens einen Versandweg auswählen (E-Mail oder Portal).');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/reports/jahresabrechnung/v2/send', {
        year,
        property_id: propertyId,
        tenant_ids: selectedIds,
        send_email: sendEmail,
        send_to_portal: sendPortal,
      });
      setResults(res.data);
    } catch (e) {
      alert('Versand fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="send-dialog">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading text-xl font-bold">Abrechnung versenden</h2>
            <p className="text-xs text-slate-500 mt-0.5">{propertyName} · Jahr {year}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XIcon weight="bold" className="w-5 h-5" /></button>
        </div>

        {!results ? (
          <>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Versandweg</label>
                <div className="flex gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="send-email-check" />
                    <span className="text-sm flex items-center gap-1"><EnvelopeSimple weight="bold" className="w-4 h-4" /> Per E-Mail (mit PDF)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={sendPortal} onChange={(e) => setSendPortal(e.target.checked)} data-testid="send-portal-check" />
                    <span className="text-sm flex items-center gap-1"><FileText weight="bold" className="w-4 h-4" /> Im Mieter-Portal ablegen</span>
                  </label>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mieter ({selectedIds.length}/{mieter.length} ausgewählt)</label>
                  <input
                    type="text"
                    placeholder="Filtern..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="border-b border-slate-200 px-2 py-1 text-xs outline-none focus:border-slate-900"
                    data-testid="send-dialog-filter"
                  />
                </div>
                <div className="border border-slate-200 max-h-64 overflow-auto">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                      <span className="font-medium">Alle gefilterten auswählen</span>
                    </label>
                  </div>
                  {filtered.map(m => (
                    <label key={m.tenant_id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={!!selected[m.tenant_id]}
                        onChange={(e) => setSelected(prev => ({ ...prev, [m.tenant_id]: e.target.checked }))}
                        data-testid={`send-check-${m.tenant_id}`}
                      />
                      <div className="flex-1 text-sm">
                        <div className="font-medium">{m.tenant_name}</div>
                        <div className="text-xs text-slate-500">{m.unit_name} · {m.tenant_email || 'Keine E-Mail'}</div>
                      </div>
                      <span className={`text-xs font-mono ${m.ergebnis === 'guthaben' ? 'text-emerald-700' : m.ergebnis === 'nachzahlung' ? 'text-red-700' : 'text-slate-500'}`}>
                        {m.saldo >= 0 ? '+' : ''}{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(m.saldo)}
                      </span>
                    </label>
                  ))}
                  {filtered.length === 0 && <div className="p-4 text-center text-slate-500 text-sm">Keine Treffer</div>}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm">Abbrechen</button>
              <button
                onClick={send}
                disabled={busy || selectedIds.length === 0}
                className="bg-violet-700 text-white px-5 py-2 font-medium hover:bg-violet-800 disabled:opacity-50 flex items-center gap-2 text-sm"
                data-testid="send-confirm-btn"
              >
                <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                {busy ? 'Sende...' : `Versenden an ${selectedIds.length} Mieter`}
              </button>
            </div>
          </>
        ) : (
          <div className="p-6 space-y-4" data-testid="send-results">
            <div className="bg-emerald-50 border border-emerald-200 p-4">
              <h3 className="font-bold text-emerald-800">Versand abgeschlossen</h3>
              <p className="text-sm text-emerald-700 mt-1">
                {results.total} Mieter verarbeitet · {results.results.filter(r => r.status === 'sent').length} erfolgreich
              </p>
            </div>
            <div className="border border-slate-200 max-h-64 overflow-auto">
              {results.results.map((r, idx) => (
                <div key={idx} className="px-3 py-2 border-b border-slate-100 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium">{r.tenant_name || r.tenant_id}</div>
                    {r.reason && <div className="text-xs text-slate-500">{r.reason}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.actions?.includes('email') && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5">E-Mail ✓</span>}
                    {r.actions?.includes('portal') && <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5">Portal ✓</span>}
                    <span className={`text-xs font-bold ${r.status === 'sent' ? 'text-emerald-700' : r.status === 'partial' ? 'text-amber-700' : 'text-red-700'}`}>
                      {r.status === 'sent' ? 'OK' : r.status === 'partial' ? 'Teilweise' : 'Fehler'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button onClick={onSent} className="bg-slate-900 text-white px-5 py-2 font-medium hover:bg-slate-800 text-sm">Schließen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
